<template>
  <div>
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <div v-if="open" class="fixed inset-0 z-40 bg-grey-900">
      <div class="flex flex-row justify-between p-4 bg-white">
        <div
          class="text-xl text-grey-700 font-bold h-full flex flex-col justify-center"
        >
          {{ $t('frontend_team_members') }}
        </div>
        <div class="flex flex-row justify-end">
          <gd-button class="mr-4" @click="close"> {{ $t('frontend_cancel') }} </gd-button>
        </div>
      </div>
      <div
        class="w-full flex flex-row justify-center py-8"
        style="max-height: calc(100vh - 64px); overflow: auto"
      >
        <div class="rounded-lg bg-white w-3/4 h-full">
          <gd-tabs :tabs="tabs">
            <template :slot="'TEAM MEMBERS'">
              <div class="p-4 vclient-table">
                <div class="flex justify-between items-center mb-4">
                  <h6>{{ $t('frontend_team_members') }}</h6>
                  <gd-button
                    size="auto"
                    class="px-8 h-10"
                    primary
                    @click="createMemberModal"
                    >{{ $t('frontend_team_members_add_new') }}</gd-button
                  >
                </div>

                <v-client-table
                  :data="teamMembers"
                  :columns="teamMembersColumns"
                  :options="teamMembersOptions"
                  class="bg-white text-center rounded border border-grey-300"
                >
                  <div slot="actions" slot-scope="props">
                    <dropdown-button>
                      <span
                        slot="link"
                        class="flex items-center h-10 font-medium pl-3 pr-2 border border-grey-500 rounded"
                      >
                        <span
                          class="mr-1 border-r border-grey-500 pr-2"
                          style="width: 65px; height: 100%; line-height: 38px"
                          >{{ $t('frontend_actions') }}</span
                        >
                        <svg
                          class="h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path
                            d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                          />
                        </svg>
                      </span>
                      <div
                        slot="dropdown"
                        class="bg-white shadow rounded border border-grey-300 overflow-hidden cursor-pointer"
                      >
                        <div
                          class="no-underline block p-2 text-grey-darkest bg-white"
                          @click="editCategory(props.row.id)"
                        >
                          {{ $t('frontend_edit') }}
                        </div>
                        <div
                          class="no-underline block p-2 text-grey-darkest bg-white"
                          @click="deleteCategory(props.row.id)"
                        >
                          {{ $t('frontend_delete') }}
                        </div>
                      </div>
                    </dropdown-button>
                  </div>
                </v-client-table>

                <gd-modal name="create-member-modal" title="New Member">
                  <div class="flex flex-col px-8 py-5">
                    <div
                      class="flex flex-col w-full mb-4 border-b border-solid border-grey-200"
                    >
                      <label class="text-grey-dark mb-2">{{ $t('frontend_team_members_role') }}</label>
                      <gd-dropdown
                        :options="memberRoleList"
                        placeolder="Select member role"
                        class="w-full selectbox"
                      ></gd-dropdown>
                      <div class="text-grey-500 mt-1 mb-4 flex justify-end">
                        <a href="#" class="text-pink">{{ $t('frontend_team_members_or_create_a_new_role') }}</a> {{ $t('frontend_team_members_roles_define_access_level') }}
                      </div>
                    </div>
                    <div class="flex flex-col w-full mb-4">
                      <label class="text-grey-dark mb-2">{{ $t('frontend_team_members_public_username') }}</label>
                      <text-input
                        class="custom-input-text"
                        v-model="member.user_name"
                        :validator="$v.member.user_name"
                        placeholder=""
                      />
                    </div>
                    <div class="flex flex-col w-full mb-4">
                      <label class="text-grey-dark mb-2">{{ $t('frontend_first_name') }}</label>
                      <text-input
                        class="custom-input-text"
                        v-model="member.first_name"
                        :validator="$v.member.first_name"
                        placeholder=""
                      />
                    </div>
                    <div class="flex flex-col w-full mb-4">
                      <label class="text-grey-dark mb-2">{{ $t('frontend_last_name') }}</label>
                      <text-input
                        class="custom-input-text"
                        v-model="member.last_name"
                        :validator="$v.member.last_name"
                        placeholder=""
                      />
                    </div>
                    <div class="flex flex-col w-full mb-4">
                      <label class="text-grey-dark mb-2">Email</label>
                      <text-input
                        class="custom-input-text"
                        v-model="member.email"
                        :validator="$v.member.email"
                        placeholder=""
                      />
                    </div>
                    <div class="flex flex-col w-full mb-4">
                      <label class="text-grey-dark mb-2">{{ $t('frontend_password') }}</label>
                      <password-input
                        v-model="member.password"
                        :validator="$v.member.password"
                        placeholder=""
                      />
                    </div>
                    <div class="flex flex-row w-full mt-4 justify-center">
                      <gd-button
                        class="text-center h-10 border-grey-1"
                        size="large"
                        @click="closeMemberModal"
                        >{{ $t('frontend_cancel') }}</gd-button
                      >
                      <gd-button
                        class="text-center h-10 ml-4"
                        size="large"
                        primary
                        @click="submitMemberData"
                        >{{ $t('frontend_done') }}</gd-button
                      >
                    </div>
                  </div>
                </gd-modal>
              </div>
            </template>
            <template :slot="'MEMBERS ROLES'">
              <div class="p-4 vclient-table">
                <div class="flex justify-between items-center mb-4">
                  <h6>{{ $t('frontend_team_members_roles') }}</h6>
                  <gd-button
                    size="auto"
                    class="px-8 h-10"
                    primary
                    @click="createRoleModal"
                    >{{ $t('frontend_team_members_add_new_role') }}</gd-button
                  >
                </div>

                <v-client-table
                  :data="membersRoles"
                  :columns="membersRolesColumns"
                  :options="membersRolesOptions"
                  class="bg-white text-center rounded border border-grey-300"
                >
                  <div slot="actions" slot-scope="props">
                    <dropdown-button>
                      <span
                        slot="link"
                        class="flex items-center h-10 font-medium pl-3 pr-2 border border-grey-500 rounded"
                      >
                        <span
                          class="mr-1 border-r border-grey-500 pr-2"
                          style="width: 65px; height: 100%; line-height: 38px"
                          >{{ $t('frontend_actions') }}</span
                        >
                        <svg
                          class="h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path
                            d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                          />
                        </svg>
                      </span>
                      <div
                        slot="dropdown"
                        class="bg-white shadow rounded border border-grey-300 overflow-hidden cursor-pointer"
                      >
                        <div
                          class="no-underline block p-2 text-grey-darkest bg-white"
                          @click="editCategory(props.row.id)"
                        >
                          {{ $t('frontend_edit') }}
                        </div>
                        <div
                          class="no-underline block p-2 text-grey-darkest bg-white"
                          @click="deleteCategory(props.row.id)"
                        >
                          {{ $t('frontend_delete') }}
                        </div>
                      </div>
                    </dropdown-button>
                  </div>
                </v-client-table>
                <gd-modal name="create-role-modal" title="New Role Access">
                  <div class="flex flex-col px-8 py-5">
                    <div
                      class="flex flex-col w-full mb-4 pb-4 border-b border-solid border-grey-200"
                    >
                      <label class="text-grey-dark mb-2">{{ $t('frontend_team_members_role_name') }}</label>
                      <text-input
                        class="custom-input-text"
                        v-model="role.role_name"
                        :validator="$v.role.role_name"
                        placeholder=""
                      />
                    </div>

                    <div
                      v-for="(value, key, index) in role.roleAccess"
                      :key="index"
                    >
                      <h5
                        class="font-bold mt-2 mb-4 pb-2 border-b border-solid"
                      >
                        {{ key }}
                      </h5>
                      <div class="flex flex-wrap">
                        <div
                          class="flex items-center mb-4 w-1/2"
                          v-for="(rolecheck, index) in value"
                          :key="index"
                        >
                          <gd-checkbox
                            class="checkbox"
                            v-model="rolecheck.value"
                          ></gd-checkbox>
                          <label class="text-sm ml-3">{{
                            rolecheck.name
                          }}</label>
                        </div>
                      </div>
                    </div>

                    <div class="flex flex-row w-full mt-4 justify-center">
                      <gd-button
                        class="text-center h-10 border-grey-1"
                        size="large"
                        @click="closeRoleModal"
                        >{{ $t('frontend_cancel') }}</gd-button
                      >
                      <gd-button
                        class="text-center h-10 ml-4"
                        size="large"
                        primary
                        @click="submitRoleData"
                        >{{ $t('frontend_done') }}</gd-button
                      >
                    </div>
                  </div>
                </gd-modal>
              </div>
            </template>
          </gd-tabs>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import GdTabs from './Tabs';
import themeConfig from '../themeConfig.js';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  components: { GdTabs, Loading },
  props: ['open'],
  data() {
    return {
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      tabs: ['TEAM MEMBERS', 'MEMBERS ROLES'],
      teamMembers: [
        { username: 'AdaLovelase', name: 'Ada Lovelace', role: 'Owner' },
        {
          username: 'Grace@AdaLovelase',
          name: 'Grace',
          role: 'Customer Support',
        },
        {
          username: 'Margret@AdaLovelase',
          name: 'Margret',
          role: 'Technical Support',
        },
        { username: 'Joan@AdaLovelase', name: 'Joan', role: 'Product Support' },
      ],
      teamMembersColumns: ['username', 'name', 'role', 'actions'],
      teamMembersOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [],
        // headings: {
        //   name: 'Category Name',
        //   blogposts: 'No of posts',
        //   actions: '',
        // },
        filterByColumn: false,
        filterable: ['name'],
        perPage: 6,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
      membersRoles: [
        { roles: 'Owner' },
        { roles: 'Customer Support' },
        { roles: 'Technical Support' },
        { roles: 'Product Support' },
      ],
      membersRolesColumns: ['roles', 'actions'],
      membersRolesOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [],
        // headings: {
        //   name: 'Category Name',
        //   blogposts: 'No of posts',
        //   actions: '',
        // },
        filterByColumn: false,
        filterable: ['roles'],
        perPage: 6,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
      memberRoleList: [
        'Merriweather, serif',
        'Roboto, sans-serif',
        'Nunito Sans, sans-serif',
      ],
      member: {
        user_name: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
      },
      role: {
        role_name: '',
        roleAccess: {
          Account: [
            {
              name: 'Profile',
              value: false,
            },
            {
              name: 'Billing',
              value: false,
            },
            {
              name: 'Staff ',
              value: false,
            },
          ],
          System: [
            {
              name: 'Contact',
              value: false,
            },
            {
              name: 'News',
              value: false,
            },
            {
              name: 'Alerts',
              value: false,
            },
          ],
        },
      },
    };
  },
  validations: {
    member: {
      user_name: {
        required,
      },
      first_name: {
        required,
      },
      last_name: {
        required,
      },
      email: {
        required,
      },
      password: {
        required,
      },
    },
    role: {
      role_name: {
        required,
      },
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    createMemberModal() {
      this.member = {};
      this.$root.$emit('modal-open', 'create-member-modal');
    },
    closeMemberModal() {
      this.$root.$emit('modal-close', 'create-member-modal');
    },
    async submitMemberData() {
      this.$v.member.$touch();
      if (!this.$v.member.$invalid) {
        this.$v.member.$reset();
        this.showLoader();
        // await axios
        //   .post('/categories', this.post)
        //   .then((result) => {
        //     this.getCategory(this.blogId);
        //     this.visibleNav = false;
        //   })
        //   .catch((error) => {
        //     console.log(error);
        //   });
        this.hideLoader();
        this.$root.$emit('modal-close', 'create-member-modal');
      }
    },
    createRoleModal() {
      // this.member = {};
      this.$root.$emit('modal-open', 'create-role-modal');
    },
    closeRoleModal() {
      this.$root.$emit('modal-close', 'create-role-modal');
    },
    async submitRoleData() {
      this.$v.role.$touch();
      if (!this.$v.role.$invalid) {
        this.$v.role.$reset();
        this.showLoader();
        // await axios
        //   .post('/categories', this.post)
        //   .then((result) => {
        //     this.getCategory(this.blogId);
        //     this.visibleNav = false;
        //   })
        //   .catch((error) => {
        //     console.log(error);
        //   });
        this.hideLoader();
        this.$root.$emit('modal-close', 'create-role-modal');
      }
    },
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style lang="scss">
.vclient-table {
  line-height: 1.25;
  .VueTables__search .VueTables__search-field {
    display: none;
  }
  .VueTables--client table {
    overflow: auto !important;
  }
  .VueTables__heading {
    color: #8f92ab;
  }
  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }
  td {
    font-weight: 200;
  }
  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }
  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid #fd6481;
  }
  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }
  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }
  .VueTables__row {
    border-bottom: 1px solid #ddd;
    td {
      &:first-child {
        width: 30%;
      }
      &:nth-child(2) {
        width: 25%;
      }
      &:nth-child(3) {
        width: 25%;
      }
      &:last-child {
        width: 20%;
      }
    }
  }
  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }
  tr.VueTables__child-row > td {
    padding: 0px !important;
  }
  thead {
    border-bottom: 1px solid #ddd;
  }
  .vue-pagination-ad {
    text-align: center;
  }
  .VueTables__limit {
    display: none;
  }
  .VuePagination__count {
    display: none;
  }
  .expandIcon {
    display: none;
  }
  .VuePagination__pagination {
    display: flex;
    margin-top: 10px;
    justify-content: flex-end;
    margin-right: 10px;
    margin-bottom: 10px;
  }

  .VuePagination__pagination-item {
    padding-left: 2px;
    padding-right: 2px;
  }
  .VueTables__search-field {
    display: flex;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #dcdcea;
  }
  .VueTables__search__input {
    border: 1px solid #eee;
    border-radius: 5px;
    margin-left: 10px;
    font-size: 14px;
    padding: 6px !important;
  }
}
.modal-textarea {
  height: 122px;
}
.modal-textarea textarea {
  padding: 16px;
}
</style>
